import { action, makeObservable } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { OrganizationEntity, organizationSchema, OrganizationsResponseApi } from '@/entities/Organization';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { NotificationsStore } from '../NotificationsStore';

export class OrganizationsStore implements ILocalStore {
  readonly meta = new MetaStore();
  readonly collection = new CollectionStore<OrganizationEntity, OrganizationEntity>({
    parseItem: (data: OrganizationEntity) => data,
    getEntityKey: (data: OrganizationEntity) => String(data.id),
  });
  private _organizationsListVisited = false;

  constructor(private readonly _notificationsStore: NotificationsStore) {
    makeObservable(this, {
      fetchOrganizations: action.bound,
    });
  }

  async fetchOrganizations(options?: { force?: boolean; silent?: boolean }): Promise<void> {
    const { force = false, silent = false } = options ?? {};

    if (!force && (this.meta.isLoading || this.meta.isLoaded)) {
      return;
    }

    if (!silent) {
      this.meta.setLoadedStartMeta();
    }

    const response = await call<OrganizationsResponseApi>({
      endpoint: ENDPOINTS.organizations,
    });

    if (response.isError) {
      if (!silent) {
        this.meta.setLoadedErrorMeta();
      }

      this._notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось загрузить организации');

      return;
    }

    const parsed = schemeParseMultiple(organizationSchema, response.data.items);

    this.collection.fromArray(parsed);

    if (!silent) {
      this.meta.setLoadedSuccessMeta();
    }
  }

  getOrganizationById = (id: string): OrganizationEntity | null => {
    return this.collection.getItemByKey(id);
  };

  get hasOrganizationsListVisited(): boolean {
    return this._organizationsListVisited;
  }

  markOrganizationsListVisited = (): void => {
    this._organizationsListVisited = true;
  };

  destroy(): void {
    this.collection.destroy();
    this.meta.destroy();
  }
}
